<template>
  <div class="c-mobile__head q-px-md q-mt-xs">
    <div class="row items-center no-wrap q-col-gutter-md">
      <div class="col-auto">
        <div class="q-py-sm">
          <router-link to="/" @click="handleLogoClick">
            <img class="c-mobile__logo" src="/static/img/logo.png" fetchpriority="high"/>
          </router-link>
        </div>
      </div>

      <div class="col flex justify-end">
        <div @click="onDeliveryClick" style="width: 100%; max-width: 250px;">
          <q-no-ssr>
            <q-field
                outlined
                dense
                bg-color="white"
                class="c-mobile__delivery"
                :label="deliveryLabel"
                stack-label
            >
              <template v-slot:control>
                <div class="self-center full-width no-outline" tabindex="-1">
                  <div class="s-font-3xs text-grey-9 text-ellipsis">
                    {{ deliveryAddress }}
                  </div>
                </div>
              </template>
            </q-field>
          </q-no-ssr>
        </div>
      </div>

      <div class="col-auto" style="">
        <div class="flex q-gutter-x-md no-wrap justify-end q-py-sm">
          <q-btn
              :icon="ICONS.phone"
              class="gt-mobile-lg c-mobile__btn"
              size="16px"
              color="dark"
              dense
              flat
              to="/catalog/search"
          />
          <q-btn
              :icon="ICONS.search"
              class="gt-mobile-md c-mobile__btn"
              size="16px"
              color="dark"
              label="поиск"
              dense
              flat
              to="/catalog/search"
          />
          <q-btn
              :icon="ICONS.notificationsNone"
              class="c-mobile__btn"
              color="dark"
              dense
              flat
              size="16px"
              to="/personal/notices"
          >
            <q-no-ssr>
              <q-badge
                  v-if="noticeUnreadCount"
                  color="red"
                  floating
                  rounded
                  class="s-font-3xs"
              >
                {{ noticeUnreadCount > 99 ? '99+' : noticeUnreadCount }}
              </q-badge>
            </q-no-ssr>
          </q-btn>
          <q-btn
              :icon="ICONS.humburger"
              flat
              dense
              size="14px"
              color="dark"
              @click="drawerMainModel = !drawerMainModel"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {ICONS} from "@/assets/icons";
import {useNoticeStore} from "@/modules/notice/store/notice";
import {useOrdersStore} from "@/modules/shop/store/orders";
import {useVorderState} from "@/modules/shop/store/vorder-state";
import {useLayoutStore} from "@/stores/layout";
import {storeToRefs} from "pinia";
import {computed} from "vue";
import {useRouter} from "vue-router";

const noticeStore = useNoticeStore()
const {unreadedCount: noticeUnreadCount} = storeToRefs(noticeStore)

const layoutStore = useLayoutStore()
const {drawerMainModel} = storeToRefs(layoutStore)

const vorderState = useVorderState()
const {attrs, currentPickupOffice, isDeliveryCourier, isDeliveryPickup} = storeToRefs(vorderState)

const ordersStore = useOrdersStore()
const {ordersActive} = storeToRefs(ordersStore)

const router = useRouter()

// Определяем emit
const emit = defineEmits<{
  logoClick: [event: MouseEvent]
}>()

/**
 * Обработчик клика по логотипу.
 * Пробрасываем событие наверх в родительский компонент.
 */
const handleLogoClick = (event: MouseEvent) => {
  emit('logoClick', event)
}

// Последний активный заказ
const lastActiveOrder = computed(() => {
  return ordersActive.value.length > 0 ? ordersActive.value[0] : null
})

// Computed для отображения информации о доставке
const deliveryLabel = computed(() => {
  if (isDeliveryCourier.value) {
    return 'Доставка'
  } else if (isDeliveryPickup.value) {
    return 'Самовывоз'
  } else {
    return 'Способ доставки'
  }
})

const deliveryAddress = computed(() => {
  let address: string = ''

  if (isDeliveryCourier.value && attrs.value.ADDRESS) {
    address = attrs.value.ADDRESS
  } else if (isDeliveryPickup.value && currentPickupOffice.value) {
    address = currentPickupOffice.value.NAME!
  } else {
    return 'выберите способ доставки'
  }

  // Удаляем "г. Иркутск", "г Иркутск" и просто "Иркутск" из адреса
  return address
      .replace(/г\.?\s*Иркутск,?\s*/gi, '')
      .replace(/Иркутск,?\s*/gi, '')
      .trim()
      .replace(/^,\s*/, '') // Удаляем запятую в начале если осталась
})

const onDeliveryClick = () => {
  router.push('/delivery-service?role=vorder')
}
</script>

<style lang="scss" scoped>

.c-mobile__logo {
  width: 81px;
  height: 30px;
}

.c-mobile__delivery {
  :deep(.q-field__control),
  :deep(.q-field__native) {
    min-height: auto;
  }

  :deep(.q-field__control) {
    padding-left: 8px !important;
    padding-right: 8px !important;

    .text-ellipsis {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 100%;
    }
  }

  :deep(.q-field__label) {
    font-size: 11px !important;
    line-height: 1.2;
    color: var(--q-primary) !important;
    transform: translateY(-3px) !important;
  }

  // Переопределяем цвет бордера на обычный серый
  :deep(.q-field--outlined .q-field__control) {
    &:before {
      border-color: rgba(0, 0, 0, 0.24) !important;
    }

    &:hover:before {
      border-color: rgba(0, 0, 0, 0.87) !important;
    }
  }

  // Убираем фокусный цвет primary
  :deep(.q-field--outlined.q-field--focused .q-field__control) {
    &:before,
    &:after {
      border-color: rgba(0, 0, 0, 0.87) !important;
    }
  }
}

.c-mobile__btn {
  :deep(.q-btn__content) {
    .block {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.54);
    }
  }
}
</style>
